// ACCS-11 — a search page's criteria live in the address (ACCS-13), and each change
// the reader makes is a step the browser's Back can undo. `useSearchParams` with
// `{ replace: true }` kept the address bookmarkable but made Back leave the page;
// pushing every write would make Back undo a search box one letter at a time.
// per docs/shape-ops-readable-urls-and-history.md §3
import { useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

/** How long after a write the next edit of the same criteria still belongs to it. */
export const CRITERIA_BURST_MS = 1000;

export interface CriteriaWriteOptions {
  /**
   * A rewrite the page makes on its own (normalising a value, dropping a criterion
   * the role may not use), never a step of the reader's: it adds no history entry.
   */
  replace?: true;
}

export type CriteriaWriter = (next: URLSearchParams, options?: CriteriaWriteOptions) => void;

interface LastWrite {
  keys: ReadonlySet<string>;
  at: number;
  search: string;
}

/** The parameters whose values differ between two addresses, repeated keys included. */
export function changedKeys(before: URLSearchParams, after: URLSearchParams): Set<string> {
  const changed = new Set<string>();
  for (const key of new Set([...before.keys(), ...after.keys()])) {
    const a = before.getAll(key);
    const b = after.getAll(key);
    if (a.length !== b.length || a.some((value, index) => value !== b[index])) changed.add(key);
  }
  return changed;
}

/**
 * Whether a write continues the previous one rather than starting a step of its own:
 * it touches only keys the previous write touched, soon after it, from the address
 * that write left. Back, Forward or any other navigation in between ends the burst.
 */
export function continuesBurst(
  changed: ReadonlySet<string>,
  previous: LastWrite | null,
  currentSearch: string,
  now: number,
): boolean {
  if (!previous) return false;
  if (previous.search !== currentSearch) return false;
  if (now - previous.at > CRITERIA_BURST_MS) return false;
  for (const key of changed) if (!previous.keys.has(key)) return false;
  return true;
}

/**
 * `useSearchParams` for a page whose criteria are the address. A reader's change
 * pushes one history entry; a burst of edits to the same criteria (typing, a date
 * being entered) replaces the entry its first edit pushed; a write that changes
 * nothing writes nothing, since React Router would push a duplicate entry and Back
 * would seem to do nothing.
 */
export function useCriteriaParams(): [URLSearchParams, CriteriaWriter] {
  const [searchParams, setSearchParams] = useSearchParams();
  const current = useRef(searchParams);
  const lastWrite = useRef<LastWrite | null>(null);

  useEffect(() => {
    current.current = searchParams;
  }, [searchParams]);

  const write = useCallback<CriteriaWriter>(
    (next, options) => {
      const changed = changedKeys(current.current, next);
      if (changed.size === 0) return;
      const now = Date.now();
      const nextSearch = next.toString();
      if (options?.replace) {
        // The page's own rewrite carries no step, and the reader's burst goes on
        // from the address it leaves.
        if (lastWrite.current) lastWrite.current = { ...lastWrite.current, search: nextSearch };
        setSearchParams(next, { replace: true });
        return;
      }
      const previous = lastWrite.current;
      const replace = continuesBurst(changed, previous, current.current.toString(), now);
      lastWrite.current = {
        keys: replace && previous ? previous.keys : changed,
        at: now,
        search: nextSearch,
      };
      // The next write may come before React re-renders with this address.
      current.current = next;
      setSearchParams(next, { replace });
    },
    [setSearchParams],
  );

  return [searchParams, write];
}
